import React, { useState, useEffect, useMemo, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import useEscapeKey from '../../../hooks/useEscapeKey';
import Badge from '../../../components/ui/Badge';
import SearchableSelect from '../../../components/ui/SearchableSelect';
import DocumentAttach from '../../../components/ui/DocumentAttach';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { DIVISION_OPTIONS, divisionLabel } from '../../../constants/divisions';
import { formatDate } from '../../../utils/formatDate';
import { formatCurrency } from '../../../utils/formatCurrency';
import { statusVariant } from '../../../utils/statusVariant';
import { supabase } from '../../../lib/supabase';
import { listProjectProfessionals, listProjectRfis, isNomenclatureTaken } from '../../../services/projectDirectivesService';
import { isStandard, nextNomenclature } from '../../../utils/directiveNomenclature';
import DirectiveDocumentsPanel from './DirectiveDocumentsPanel';
import DirectiveHistoryList, { useDirectiveHistory } from './DirectiveHistoryList';
import { OUTCOME_VARIANT } from './DirectivesTable';

/**
 * Directive : une seule modale pour consulter, modifier et creer.
 *
 *   mode 'view'   fiche en lecture (clic sur la ligne ou la nomenclature)
 *   mode 'edit'   les memes emplacements deviennent des champs, sur place :
 *                 Modifier -> 'edit', Annuler / Enregistrer -> retour a 'view'
 *   mode 'create' champs vides ; les pieces sont mises en attente et partent
 *                 apres l'insert (le chemin de stockage depend de l'id)
 *
 * Chaque valeur en lecture occupe la place exacte de son champ (meme libelle,
 * meme hauteur) : la bascule lecture <-> edition ne fait pas sauter la page.
 *
 * `directive` vient de la liste de la page : apres un enregistrement, la page
 * relit la liste et la modale affiche les valeurs a jour.
 *
 * Pas de portail : la modale est montee AVANT les autres modales de directive
 * (historique complet, pieces, confirmations), qui s'ouvrent par-dessus.
 */

const HISTORY_PREVIEW = 5;

// Hauteur UNIQUE d'un champ d'une ligne, dans les 3 modes : input texte, input
// date, SearchableSelect (via son input) et valeur en lecture. 36 px.
// La bascule consultation <-> edition ne deplace donc aucun champ.
const FIELD_H = 'h-9';
// SearchableSelect ne prend pas de classe pour son input : on la pose par le
// conteneur (descendant), sans toucher au composant partage.
const SELECT_H = '[&_input]:h-9';

// Garde l'element deja lie a la directive dans la liste d'options, meme s'il
// n'y figure pas (professionnel retire du projet ou inactif, QRT hors liste) :
// sinon le selecteur s'afficherait vide alors que la directive en a un.
const withCurrent = (list, current) =>
  current?.id && !(list || [])?.some((item) => item?.id === current?.id)
    ? [...(list || []), current]
    : (list || []);

const formOf = (directive) => ({
  division: directive?.division || '',
  professionalId: directive?.professional_id || '',
  title: directive?.title || '',
  nomenclature: directive?.nomenclature || '',
  dateDirective: directive?.date_directive ? String(directive?.date_directive)?.slice(0, 10) : '',
  rfiId: directive?.rfi_id || '',
  description: directive?.description || '',
});

// ─── Titre de section avec barre orange (pleine largeur) ─────────────────────
const SectionTitle = ({ children, action }) => (
  <div className="flex items-center justify-between gap-2 mb-1.5">
    <div className="flex items-center gap-2">
      <span className="w-[3px] h-3.5 rounded-full bg-primary flex-shrink-0" />
      <span className="text-[10px] font-semibold text-primary uppercase tracking-[0.12em]">{children}</span>
    </div>
    {action}
  </div>
);

// Valeur en lecture : une ligne, a la hauteur exacte d'un champ (FIELD_H, texte
// 12 px). Une valeur trop longue est tronquee, complete au survol.
const ReadValue = ({ value, className = '' }) => (
  <div className={`${FIELD_H} flex items-center px-3 text-xs rounded-lg border border-gray-100 bg-gray-50 min-w-0 ${className}`}>
    {value != null && value !== ''
      ? <span className="text-gray-800 truncate" title={String(value)}>{value}</span>
      : <span className="text-gray-300">—</span>}
  </div>
);

const LinkButton = ({ icon, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
  >
    {icon && <Icon name={icon} size={12} />}
    {children}
  </button>
);

const DirectiveModal = ({
  mode: initialMode = 'view',
  directive = null,
  directives = [],
  extra = null,
  project = null,
  projectId,
  onClose,
  onCreate,
  onUpdate,
  onDocumentsChanged,
  onFullHistory,
  onOpenExtra,
  onOpenRfis,
  onLinkExtra,
  onUnlinkExtra,
}) => {
  const { t, language } = useTranslation();
  const [mode, setMode] = useState(initialMode);
  const isCreate = mode === 'create';
  const isView = mode === 'view';
  const isEditing = mode === 'edit' || isCreate;

  const [form, setForm] = useState(() => formOf(isCreate ? null : directive));
  const [initialForm, setInitialForm] = useState(() => formOf(isCreate ? null : directive));
  const [stagedFiles, setStagedFiles] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [rfis, setRfis] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // Options des selecteurs chargees : en edition, Enregistrer attend ce moment.
  const [optionsReady, setOptionsReady] = useState(false);
  // Abandon des modifications a confirmer : null, 'view' (Echap) ou 'close' (✕).
  const [confirmDiscard, setConfirmDiscard] = useState(null);
  const [creatorName, setCreatorName] = useState(null);
  // Derniere nomenclature proposee automatiquement (creation) : on ne remplace
  // la saisie que si elle est vide ou contient encore cette proposition.
  const suggestionRef = useRef(null);

  const history = useDirectiveHistory(isCreate ? null : directive?.id, directive);
  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initialForm), [form, initialForm]);
  // La nomenclature a-t-elle ete modifiee (par rapport a la valeur d'origine) ?
  const nomenclatureChanged = (form?.nomenclature || '')?.trim() !== (initialForm?.nomenclature || '')?.trim();

  // Options des selecteurs : chargees a la premiere entree en edition / creation.
  useEffect(() => {
    if (isView || optionsReady || !projectId) return undefined;
    let alive = true;
    Promise.all([listProjectProfessionals(projectId), listProjectRfis(projectId)])
      ?.then(([pros, qrts]) => {
        if (!alive) return;
        setProfessionals(withCurrent(pros, directive?.professional));
        setRfis(withCurrent(qrts, directive?.rfi));
        setOptionsReady(true);
      })
      ?.catch(() => {
        if (!alive) return;
        setProfessionals(withCurrent([], directive?.professional));
        setRfis(withCurrent([], directive?.rfi));
        setOptionsReady(true);
      });
    return () => { alive = false; };
  }, [isView, optionsReady, projectId, directive?.professional, directive?.rfi]);

  // Nom de la personne qui a saisi la directive (lecture simple de user_profiles).
  useEffect(() => {
    if (isCreate || !directive?.created_by) { setCreatorName(null); return undefined; }
    let alive = true;
    supabase
      ?.from('user_profiles')
      ?.select('full_name')
      ?.eq('id', directive?.created_by)
      ?.maybeSingle()
      ?.then(({ data }) => { if (alive) setCreatorName(data?.full_name || null); });
    return () => { alive = false; };
  }, [isCreate, directive?.created_by]);

  // Lecture -> edition : les champs partent des valeurs affichees.
  const startEdit = () => {
    const values = formOf(directive);
    setForm(values);
    setInitialForm(values);
    setErrors({});
    setSubmitError(null);
    setMode('edit');
  };

  // Edition -> lecture, sans enregistrer : valeurs d'origine.
  const cancelEdit = () => {
    setForm(initialForm);
    setErrors({});
    setSubmitError(null);
    setConfirmDiscard(null);
    setMode('view');
  };

  // Echap : le panneau des pieces passe avant (renommage, confirmation) ; puis
  // edition -> lecture (avec confirmation si des champs ont change) ; puis ferme.
  useEscapeKey(() => {
    if (mode === 'edit') {
      if (dirty) setConfirmDiscard('view');
      else cancelEdit();
      return;
    }
    onClose?.();
  }, !saving && !confirmDiscard);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev?.[field] ? { ...prev, [field]: null } : prev));
  };

  // Division : en creation, propose la prochaine nomenclature libre de la
  // discipline, sans jamais ecraser une saisie de l'utilisateur.
  const handleDivisionChange = (value) => {
    handleChange('division', value);
    if (!isCreate) return;
    const current = form?.nomenclature?.trim();
    if (current && current !== suggestionRef?.current) return;
    const suggestion = nextNomenclature(directives, value);
    suggestionRef.current = suggestion;
    handleChange('nomenclature', suggestion || '');
  };

  const validate = () => {
    const next = {};
    if (!form?.division) next.division = t('extras.directives.create.divisionRequired');
    if (!form?.professionalId) next.professionalId = t('extras.directives.create.professionalRequired');
    if (!form?.title?.trim()) next.title = t('extras.directives.create.titleRequired');
    // Le format standard n'est qu'une suggestion (aide sous le champ) : seuls
    // le vide et le doublon bloquent l'enregistrement.
    if (!form?.nomenclature?.trim()) next.nomenclature = t('extras.directives.create.nomenclatureRequired');
    if (!form?.dateDirective) next.dateDirective = t('extras.directives.create.dateRequired');
    if (!form?.description?.trim()) next.description = t('extras.directives.create.descriptionRequired');
    return next;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSaving(true);
    setSubmitError(null);
    try {
      // Doublon (meme normalisation que l'index) : verifie avant d'ecrire.
      if (nomenclatureChanged && await isNomenclatureTaken(projectId, form?.nomenclature, directive?.id || null)) {
        setErrors((prev) => ({ ...prev, nomenclature: t('extras.directives.create.nomenclatureTaken') }));
        return;
      }
      if (isCreate) {
        await onCreate?.({ ...form, files: stagedFiles });
        onClose?.();
        return;
      }
      await onUpdate?.(directive?.id, form);
      setInitialForm(form);
      setMode('view');
    } catch (err) {
      // Doublon refuse par l'index (deux saisies simultanees) : sous le champ.
      if (err?.code === 'NOMENCLATURE_TAKEN') {
        setErrors((prev) => ({ ...prev, nomenclature: t('extras.directives.create.nomenclatureTaken') }));
        return;
      }
      setSubmitError(
        isCreate
          ? err?.code === '23505'
            ? t('extras.directives.create.numberCollision')
            : t('extras.directives.create.error')
          : t('extras.directives.edit.error')
      );
    } finally {
      setSaving(false);
    }
  };

  const fieldClass = (key) =>
    `w-full px-3 py-2 text-xs leading-[18px] border rounded-lg outline-none transition-colors placeholder:text-gray-400 placeholder:font-normal ${
      errors?.[key]
        ? 'border-red-300 bg-red-50'
        : 'border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20'
    }`;

  // Champ d'une ligne (texte, date) : meme hauteur que les selecteurs et la lecture.
  const inputClass = (key) => `${fieldClass(key)} ${FIELD_H}`;

  const label = (text, required = false, action = null) => (
    <div className="flex items-center justify-between gap-2 mb-1">
      <label className="block text-[11px] font-medium text-[#FF6B35]">
        {text} {required && isEditing && <span className="text-red-500">*</span>}
      </label>
      {action}
    </div>
  );

  const fieldError = (key) => (errors?.[key] ? <p className="mt-1 text-xs text-red-500">{errors?.[key]}</p> : null);

  if (!isCreate && !directive) return null;

  const outcome = directive?.outcome || 'pending';
  const rfi = directive?.rfi || null;
  const linkedExtra = !isCreate && directive?.extra_id ? (extra || directive?.extra || null) : null;

  // Montants de l'extra lie : soumis = cout + profit (comme le tableau des extras),
  // accepte seulement une fois approuve.
  const submittedAmount = linkedExtra && (linkedExtra?.costSubmitted != null || linkedExtra?.amount != null)
    ? (parseFloat(linkedExtra?.costSubmitted ?? linkedExtra?.amount) || 0) + (parseFloat(linkedExtra?.profit) || 0)
    : null;
  const acceptedAmount = linkedExtra?.status === 'approved' && linkedExtra?.costAccepted != null
    ? parseFloat(linkedExtra?.costAccepted) || 0
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (isView && e?.target === e?.currentTarget) onClose?.(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">
        {/* En-tete orange */}
        <div style={{ background: 'linear-gradient(135deg, #FF6B2C 0%, #FF8C55 100%)' }} className="px-5 py-3 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-orange-200 text-[9px] font-semibold tracking-[0.18em] uppercase mb-0.5">
                {t('extras.directives.detail.eyebrow')}
              </p>
              <h2 className="text-white font-bold text-[15px] leading-tight truncate">
                {isCreate ? t('extras.directives.create.title') : (directive?.nomenclature || '—')}
              </h2>
              <p className="text-orange-100/90 text-[11px] mt-0.5 font-medium tracking-wide truncate">
                {isCreate
                  ? t('extras.directives.create.subtitle')
                  : directive?.number != null
                    ? t('extras.directives.documentsSubtitle', { number: directive?.number })
                    : ''}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
              {mode === 'edit' && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/20 text-white">
                  {t('extras.directives.edit.mode')}
                </span>
              )}
              {!isCreate && (
                <Badge variant={OUTCOME_VARIANT?.[outcome] || 'grey'}>
                  {t(`extras.directives.outcome.${outcome}`)}
                </Badge>
              )}
              <button
                type="button"
                onClick={() => {
                  if (mode === 'edit' && dirty) setConfirmDiscard('close');
                  else onClose?.();
                }}
                className="p-1 rounded-lg bg-white/15 hover:bg-white/25 text-white transition-colors"
              >
                <Icon name="X" size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Corps — defile entre l'en-tete et le pied */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {submitError && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
              <Icon name="AlertCircle" size={15} />{submitError}
            </div>
          )}

          {/* Deux colonnes (5/7) ; une seule sous 900 px */}
          <div className="grid grid-cols-1 min-[900px]:grid-cols-12 gap-x-5 gap-y-3">
            {/* Gauche : classement et liens */}
            <div className="min-[900px]:col-span-5 space-y-3">
              {/* Division */}
              <div>
                {label(t('extras.directives.create.divisionLabel'), true)}
                {isEditing ? (
                  <>
                    <SearchableSelect
                      options={DIVISION_OPTIONS?.map((opt) => ({ value: opt?.value, label: t(`extras.divisions.${opt?.key}`) }))}
                      value={form?.division || null}
                      onChange={(val) => handleDivisionChange(val || '')}
                      placeholder={t('extras.directives.create.divisionPlaceholder')}
                      size="md"
                      className={SELECT_H}
                    />
                    {fieldError('division')}
                  </>
                ) : (
                  <ReadValue value={directive?.division ? divisionLabel(t, directive?.division) : null} />
                )}
              </div>

              {/* Professionnel */}
              <div>
                {label(t('extras.directives.create.professionalLabel'), true)}
                {isEditing ? (
                  <>
                    <SearchableSelect
                      options={professionals?.map((p) => ({
                        value: p?.id,
                        label: p?.company_name || '—',
                        searchText: `${p?.company_name || ''} ${p?.profession || ''}`,
                      }))}
                      value={form?.professionalId || null}
                      onChange={(val) => handleChange('professionalId', val || '')}
                      placeholder={t('extras.directives.create.professionalPlaceholder')}
                      size="md"
                      className={SELECT_H}
                    />
                    {optionsReady && professionals?.length === 0 && (
                      <p className="mt-1 text-[10px] text-gray-400">{t('extras.directives.create.noProfessionals')}</p>
                    )}
                    {fieldError('professionalId')}
                  </>
                ) : (
                  <ReadValue value={directive?.professional?.company_name} />
                )}
              </div>

              {/* Nomenclature */}
              <div>
                {label(t('extras.directives.create.nomenclatureLabel'), true)}
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      value={form?.nomenclature}
                      onChange={(e) => handleChange('nomenclature', e?.target?.value?.toUpperCase())}
                      placeholder={t('extras.directives.create.nomenclaturePlaceholder')}
                      className={inputClass('nomenclature')}
                    />
                    {fieldError('nomenclature')}
                  </>
                ) : (
                  <ReadValue value={directive?.nomenclature} />
                )}
                {/* Aide affichee en lecture comme en edition : meme hauteur, aucun saut.
                    Hors format standard : suggestion discrete, jamais bloquante. */}
                {isEditing && !errors?.nomenclature && form?.nomenclature?.trim() && !isStandard(form?.nomenclature) ? (
                  <p className="mt-0.5 text-[10px] text-amber-600">{t('extras.directives.create.nomenclatureSuggest')}</p>
                ) : (
                  <p className="mt-0.5 text-[10px] text-gray-400">{t('extras.directives.create.nomenclatureHint')}</p>
                )}
              </div>

              {/* Date de la directive (reception) */}
              <div>
                {label(t('extras.directives.create.dateLabel'), true)}
                {isEditing ? (
                  <>
                    <input
                      type="date"
                      value={form?.dateDirective}
                      onChange={(e) => handleChange('dateDirective', e?.target?.value)}
                      className={inputClass('dateDirective')}
                    />
                    {fieldError('dateDirective')}
                  </>
                ) : (
                  <ReadValue value={directive?.date_directive ? formatDate(String(directive?.date_directive)?.slice(0, 10), language) : null} />
                )}
                {/* Aide affichee en lecture comme en edition : meme hauteur, aucun saut. */}
                <p className="mt-0.5 text-[10px] text-gray-400">{t('extras.directives.create.dateHint')}</p>
              </div>

              {/* QRT d'origine */}
              <div>
                {label(
                  t('extras.directives.create.rfiLabel'),
                  false,
                  isView && rfi ? <LinkButton icon="ExternalLink" onClick={onOpenRfis}>{t('extras.directives.detail.openRfis')}</LinkButton> : null
                )}
                {isEditing ? (
                  <SearchableSelect
                    options={rfis?.map((r) => ({
                      value: r?.id,
                      label: `${r?.rfi_number || '—'}${r?.subject ? ` · ${r?.subject}` : ''}`,
                      searchText: `${r?.rfi_number || ''} ${r?.subject || ''}`,
                    }))}
                    value={form?.rfiId || null}
                    onChange={(val) => handleChange('rfiId', val || '')}
                    placeholder={t('extras.directives.create.rfiPlaceholder')}
                    allowNone={true}
                    size="md"
                    className={SELECT_H}
                  />
                ) : rfi ? (
                  <div className={`${FIELD_H} flex items-center gap-2 px-3 text-xs rounded-lg border border-gray-100 bg-gray-50 min-w-0`}>
                    <span
                      className="text-gray-800 truncate flex-1 min-w-0"
                      title={`${rfi?.rfi_number || ''}${rfi?.subject ? ` · ${rfi?.subject}` : ''}`}
                    >
                      {rfi?.rfi_number || '—'}{rfi?.subject ? ` · ${rfi?.subject}` : ''}
                    </span>
                    {rfi?.status && (
                      <span className="flex-shrink-0 text-[10px] text-gray-500">{t(`rfi.status.${rfi?.status}`)}</span>
                    )}
                  </div>
                ) : (
                  <ReadValue value={t('extras.directives.detail.noneF')} className="[&>span]:text-gray-400" />
                )}
              </div>

              {/* Extra / credit lie — en lecture ; Relier / Delier dans la ligne, en
                  consultation comme en edition. Une seule ligne (FIELD_H). */}
              {!isCreate && (
                <div>
                  {label(
                    t('extras.directives.detail.extra'),
                    false,
                    isView && linkedExtra ? (
                      <LinkButton icon="ArrowRight" onClick={() => onOpenExtra?.(directive?.extra_id)}>
                        {t('extras.directives.detail.viewInExtras')}
                      </LinkButton>
                    ) : null
                  )}
                  {linkedExtra ? (
                    <div
                      className={`${FIELD_H} flex items-center gap-2 px-3 text-xs rounded-lg border border-gray-100 bg-gray-50 min-w-0`}
                      title={[
                        submittedAmount != null ? `${t('extras.directives.detail.amountSubmitted')} : ${formatCurrency(submittedAmount)}` : null,
                        acceptedAmount != null ? `${t('extras.directives.detail.amountAccepted')} : ${formatCurrency(acceptedAmount)}` : null,
                      ]?.filter(Boolean)?.join(' · ') || undefined}
                    >
                      <span className="font-medium text-gray-800 flex-shrink-0">{linkedExtra?.coNumber || linkedExtra?.co_number || '—'}</span>
                      {linkedExtra?.type && <span className="text-[11px] text-gray-500 flex-shrink-0">{t(`extras.types.${linkedExtra?.type}`)}</span>}
                      {(acceptedAmount ?? submittedAmount) != null && (
                        <span className="text-[11px] text-gray-500 tabular-nums truncate">{formatCurrency(acceptedAmount ?? submittedAmount)}</span>
                      )}
                      <span className="flex-1" />
                      {linkedExtra?.status && (
                        <span className="flex-shrink-0">
                          <Badge variant={statusVariant('extras', linkedExtra?.status)}>{t(`status.${linkedExtra?.status}`)}</Badge>
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => onUnlinkExtra?.(directive)}
                        className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
                      >
                        <Icon name="Unlink" size={12} />
                        {t('extras.directives.link.unlink')}
                      </button>
                    </div>
                  ) : (
                    <div className={`${FIELD_H} flex items-center justify-between gap-2 px-3 text-xs rounded-lg border border-gray-100 bg-gray-50`}>
                      <span className="text-gray-400">{t('extras.directives.detail.noneM')}</span>
                      {/* Relier seulement tant que la directive est « A evaluer ». */}
                      {(directive?.outcome || 'pending') === 'pending' && (
                        <button
                          type="button"
                          onClick={() => onLinkExtra?.(directive)}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
                        >
                          <Icon name="Link" size={12} />
                          {t('extras.directives.link.link')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Saisie par / le — lecture */}
              {!isCreate && (
                <div className={`grid grid-cols-2 gap-3 ${mode === 'edit' ? 'opacity-60' : ''}`}>
                  <div>
                    {label(t('extras.directives.detail.enteredBy'))}
                    <ReadValue value={creatorName} />
                  </div>
                  <div>
                    {label(t('extras.directives.detail.enteredOn'))}
                    <ReadValue value={directive?.created_at ? formatDate(directive?.created_at, language) : null} />
                  </div>
                </div>
              )}
            </div>

            {/* Droite : contenu */}
            <div className="min-[900px]:col-span-7 flex flex-col gap-3 min-w-0">
              {/* Titre */}
              <div>
                {label(t('extras.directives.create.titleLabel'), true)}
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      value={form?.title}
                      onChange={(e) => handleChange('title', e?.target?.value)}
                      placeholder={t('extras.directives.create.titlePlaceholder')}
                      className={inputClass('title')}
                    />
                    {fieldError('title')}
                  </>
                ) : (
                  <ReadValue value={directive?.title} />
                )}
              </div>

              {/* Description — meme hauteur en lecture et en edition */}
              <div className="flex-1 flex flex-col">
                {label(t('extras.directives.create.descriptionLabel'), true)}
                {isEditing ? (
                  <>
                    <textarea
                      rows={10}
                      value={form?.description}
                      onChange={(e) => handleChange('description', e?.target?.value)}
                      placeholder={t('extras.directives.create.descriptionPlaceholder')}
                      className={`${fieldClass('description')} flex-1 resize-y min-h-[13rem] !leading-relaxed`}
                    />
                    {fieldError('description')}
                  </>
                ) : (
                  <div className="flex-1 min-h-[13rem] px-3 py-2 text-xs leading-relaxed rounded-lg border border-gray-100 bg-gray-50 whitespace-pre-line break-words">
                    {directive?.description
                      ? <span className="text-gray-800">{directive?.description}</span>
                      : <span className="text-gray-300">—</span>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pieces jointes — pleine largeur */}
          <div className="border-t border-gray-100 pt-3">
            <SectionTitle>{t('extras.directives.create.documentsLabel')}</SectionTitle>
            {isCreate ? (
              <DocumentAttach
                documents={stagedFiles?.map((f, i) => ({
                  id: `staged-${i}-${f?.name}`,
                  file_name: f?.name,
                  mime_type: f?.type,
                  size_bytes: f?.size,
                }))}
                onAdd={(file) => setStagedFiles((prev) => [...prev, file])}
                onRemove={(doc) =>
                  setStagedFiles((prev) => prev?.filter((f, i) => `staged-${i}-${f?.name}` !== doc?.id))
                }
                hint={t('extras.directives.create.documentsHint')}
                accept="application/pdf"
              />
            ) : (
              <DirectiveDocumentsPanel
                directive={directive}
                project={project}
                active
                onCountChange={onDocumentsChanged}
              />
            )}
          </div>

          {/* Historique — lecture, les derniers changements */}
          {!isCreate && (
            <div className={`border-t border-gray-100 pt-3 ${mode === 'edit' ? 'opacity-60' : ''}`}>
              <SectionTitle
                action={<LinkButton icon="History" onClick={() => onFullHistory?.(directive)}>{t('extras.directives.detail.fullHistory')}</LinkButton>}
              >
                {t('extras.directives.history.title')}
              </SectionTitle>
              <DirectiveHistoryList history={history} limit={HISTORY_PREVIEW} />
            </div>
          )}
        </div>

        {/* Pied */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0">
          {isView ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('extras.directives.detail.close')}
              </button>
              <button
                type="button"
                onClick={startEdit}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
              >
                <Icon name="Pencil" size={13} />
                {t('extras.directives.actions.edit')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={isCreate ? onClose : cancelEdit}
                disabled={saving}
                className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-60"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving || (mode === 'edit' && !optionsReady)}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60"
              >
                {saving
                  ? <Icon name="Loader2" size={13} className="animate-spin" />
                  : <Icon name={isCreate ? 'Plus' : 'Check'} size={13} />}
                {saving
                  ? t('extras.directives.create.saving')
                  : isCreate ? t('extras.directives.create.submit') : t('extras.directives.edit.submit')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Abandonner les modifications ? (Echap ou ✕ avec des champs modifies) */}
      <ConfirmDialog
        isOpen={!!confirmDiscard}
        title={t('extras.directives.edit.discardTitle')}
        message={t('extras.directives.edit.discardMessage')}
        confirmVariant="danger"
        icon="AlertTriangle"
        confirmLabel={t('extras.directives.edit.discardConfirm')}
        cancelLabel={t('extras.directives.edit.keepEditing')}
        onConfirm={() => (confirmDiscard === 'close' ? onClose?.() : cancelEdit())}
        onCancel={() => setConfirmDiscard(null)}
      />
    </div>
  );
};

export default DirectiveModal;
